import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined';
import React, { useEffect, useMemo, useState } from 'react';
import AlertModal from '../../../../Components/AlertModal/AlertModal';
import DataSourceTag from '../../../../Components/DataSourceTag/DataSourceTag';
import scssVariables from '../../../../Config.module.scss';
import applicationSettings from '../../../../Core/Settings/ApplicationSettings';
import {
    CommodityMonthlyDomesticModel,
    CommodityMonthlyExportModel,
    CommodityMonthlyProductionModelSeasonalYearSummaryModel,
    Currency,
    DataSeries,
    DemeterDataFrequency,
    DemeterFeatureType,
    DemeterRegion,
    DemeterTableDefinitionType,
    SeasonalYearAverageModel,
    SeasonalYearByWeekAverageModel,
    UnitOfMeasure,
} from '../../../../Generated/Raven-Demeter';
import useSeasonalApi, { SeasonalMonthlyRowModel } from '../../../Apis/Hooks/useSeasonalApiHook';
import tableCsvDownloadService from '../../../Services/Download/DownloadService';
import featureFlagsService from '../../../Services/FeatureFlags/FeatureFlagsService';
import useFeatureFlag from '../../../Services/FeatureFlags/useFeatureFlagHook';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { EventActionsEnum, EventCategoriesEnum, EventDataTargetsEnum } from '../../../Services/Logging/DataLayerDefinitions';
import loggingService from '../../../Services/Logging/LoggingService';
import { TrackingEvent } from '../../../Services/Tracking/TrackingService';
import chartService from '../../Charts/ChartService';
import LinkButton, { LinkButtonType } from '../../Form/Buttons/LinkButton';
import Switch from '../../Form/Buttons/Switch';
import { ComponentHeaderRightSideOptions } from '../../Form/Inputs/ComponentMoreOptionsHorizontalDots';
import IconMenuMultiStageDropdown, { INestedOption } from '../../Form/Inputs/IconMenuMultiStageDropdown';
import ComponentHeader from '../../Headers/ComponentHeader';
import EmailLink from '../../Navigation/EmailLink/EmailLink';
import { IRegionCommoditySelection } from '../../Navigation/Hooks/useRegionCommodityNavigationHook';
import useTableDefinition from '../../Navigation/Hooks/useTableDefinitionHook';
import { SeasonalTableReferenceType } from './SeasonalTableDefinitions';
import SeasonalTableRaw from './SeasonalTableRaw';
import styles from './SeasonalTables.module.scss';

interface ISeasonalTablesProps {
    title: string;
    tableDefinitionType: DemeterTableDefinitionType;
    regionCommoditySelection: IRegionCommoditySelection;
    aggregateRegions?: DemeterRegion[];
    isXlsxDownloadAvailable?: boolean;
    unitOfMeasure?: UnitOfMeasure;
    currency?: Currency;
    showPercentTable?: boolean;
    isNativeCurrency?: boolean;
    isNativeUnitOfMeasure?: boolean;
    testId?: string;
    track: TrackingEvent;
}

const numberOfYearsToAdd = 5;
const minimumNumberOfYears = 2;

type MultiYearAverageType = CommodityMonthlyExportModel &
    CommodityMonthlyProductionModelSeasonalYearSummaryModel &
    Partial<SeasonalYearAverageModel & SeasonalYearByWeekAverageModel> & {
        numberOfYears: number;
        averageValue: number;
    };

const SeasonalTables: React.FC<ISeasonalTablesProps> = (props: ISeasonalTablesProps) => {
    // States.
    const [years, setYears] = useState<number[]>([]);
    const [displayThreeYearAverageValues, setDisplayThreeYearAverageValues] = useState<boolean>(false);
    const [displayFiveYearAverageValues, setDisplayFiveYearAverageValues] = useState<boolean>(false);
    const [displayQuarterlyAndYearlyValues, setDisplayQuarterlyAndYearlyValues] = useState<boolean>(false);
    const [showExportModal, setShowExportModal] = useState(false);
    const [, tableDefinitionCommodity] = useTableDefinition(props.tableDefinitionType, props.regionCommoditySelection);

    // Custom hooks.
    const [translations] = useLanguage();
    const isExportDataOn = useFeatureFlag(featureFlagsService.getFeatureType(DemeterFeatureType.DownloadsGeneral, props.tableDefinitionType));
    const seasonalData = useSeasonalApi(
        props.tableDefinitionType,
        props.regionCommoditySelection,
        props.aggregateRegions,
        DemeterDataFrequency.Monthly,
        props.unitOfMeasure,
        props.currency,
        years,
        true,
    );

    const seasonalMonthlyData = useMemo(() => seasonalData?.rows?.map((row) => row as SeasonalMonthlyRowModel).filter((row) => row) ?? [], [seasonalData]);
    const threeYearData = useMemo(
        () => seasonalData?.averages?.find((row) => row.numberOfYears === 3) ?? ([] as Partial<SeasonalYearAverageModel & SeasonalYearByWeekAverageModel>),
        [seasonalData],
    );
    const fiveYearData = useMemo(
        () => seasonalData?.averages?.find((row) => row.numberOfYears === 5) ?? ([] as Partial<SeasonalYearAverageModel & SeasonalYearByWeekAverageModel>),
        [seasonalData],
    );

    // Page constants.
    const hasRestrictedDataSource = chartService.isRestrictedDataSource(seasonalData?.dataSourceTag);
    const currencyText = useMemo(() => translations.currency[seasonalData?.currency!], [seasonalData]);
    const unitOfMeasureText = useMemo(() => translations.unitOfMeasure[seasonalData?.unitOfMeasure!], [seasonalData]);

    const pageSubtitle = useMemo(() => `${currencyText || ''} ${currencyText && unitOfMeasureText ? '/' : ''} ${unitOfMeasureText || ''}`, [seasonalData]);

    const handleAddYears = () => {
        const currentYears = seasonalData?.rows?.map((x) => x.year!) ?? [];
        const oldestYear = Math.min(...currentYears);

        for (let i = 1; i <= numberOfYearsToAdd; i += 1) {
            currentYears.unshift(oldestYear - i);
        }

        setYears(currentYears);

        loggingService.trackEventWithAnalytics(
            EventActionsEnum.ButtonClick,
            EventCategoriesEnum.AddedYearsToSeasonalDataTables,
            '',
            EventDataTargetsEnum.SeasonalDataTables,
        );
    };

    const handleRemoveYears = () => {
        const filteredYears = years.filter((_year, index) => index >= numberOfYearsToAdd);
        setYears(filteredYears?.length > minimumNumberOfYears ? filteredYears : filteredYears.filter((x, index) => index >= minimumNumberOfYears));

        loggingService.trackEventWithAnalytics(
            EventActionsEnum.ButtonClick,
            EventCategoriesEnum.RemovedYearsFromSeasonalDataTables,
            '',
            EventDataTargetsEnum.SeasonalDataTables,
        );
    };

    const buildSeasonalCsv = (type: SeasonalTableReferenceType): DataSeries[] | undefined => {
        if (seasonalMonthlyData.length === 0 || seasonalMonthlyData[0].monthlyValues.length === 0) {
            return [];
        }

        const isValueType = type === 'valueCsv' || type === 'valueXlsx';
        const isAverage = seasonalMonthlyData[0].monthlyValues[0].alwaysShowActualValue;
        const rows = [...seasonalMonthlyData] as MultiYearAverageType[];

        if (displayFiveYearAverageValues && isValueType) {
            rows.unshift(fiveYearData as MultiYearAverageType);
        }

        if (displayThreeYearAverageValues && isValueType) {
            rows.unshift(threeYearData as MultiYearAverageType);
        }

        const newData = rows.map((row) => {
            if (!row.monthlyValues) {
                return {};
            }

            const isMultiYearAverageType = !!row.numberOfYears;

            const averageOrTotalsData = displayQuarterlyAndYearlyValues
                ? [
                      ...(row.quarterlyValues ?? []).map((x) => ({
                          value: isValueType ? `${isAverage ? x.valueAverage : x.valueTotal}` : `${x.monthOverMonthChangePercentAverage}`,
                          rawDate: x.quarter!,
                          date: isValueType
                              ? `${translations.words.quarterShort} ${x.quarter} ${isAverage ? translations.words.average : translations.words.total}`
                              : `${translations.words.quarterShort} ${x.quarter} ${translations.words.change}`,
                          isActualValue: x.isActualValue,
                      })),
                      {
                          value: isValueType
                              ? `${isAverage ? row.yearlyValues?.valueAverage : row.yearlyValues?.valueTotal}`
                              : `${row.yearlyValues?.monthOverMonthChangePercentAverage}`,
                          date: isValueType
                              ? `${translations.words.yearly} ${isAverage ? translations.words.average : translations.words.total}`
                              : `${translations.words.yearly} ${translations.words.change}`,
                          rawDate: row.yearlyValues?.year!,
                          isActualValue: row.yearlyValues?.isActualValue,
                      },
                  ]
                : [];

            // Fill in missing months at the start of the year with null values
            const filteredMonthlyValues = row.monthlyValues.filter((x, index) => !x.asOfDate || x.asOfDate !== row.monthlyValues[index + 1]?.asOfDate);
            const firstMonthNumber = filteredMonthlyValues[0]?.month;
            for (let monthNumber = 1; monthNumber < firstMonthNumber; monthNumber += 1) {
                filteredMonthlyValues.unshift({
                    month: monthNumber,
                    asOfDate: `${row.year}-${String(monthNumber).padStart(2, '0')}-01`,
                    value: null,
                    monthOverMonthChangePercent: null,
                    isActualValue: true,
                } as CommodityMonthlyDomesticModel);
            }

            return {
                label: row.year ? `${row.year}` : `${row.numberOfYears} ${translations.words.year} ${translations.words.average}`,
                data: [
                    ...filteredMonthlyValues.map((x) => ({
                        value: isValueType ? `${x.value ?? (x as MultiYearAverageType).averageValue ?? ''}` : `${x.monthOverMonthChangePercent ?? ''}`,
                        date: formattingService.toMonth(x.month),
                        rawDate: x.asOfDate ? new Date(x.asOfDate) : (x as MultiYearAverageType).numberOfYears,
                        isActualValue: isMultiYearAverageType ? true : x.isActualValue,
                    })),
                    ...averageOrTotalsData,
                ],
            };
        });

        return newData;
    };

    const handleDisplayAveragesToggle = () => {
        setDisplayQuarterlyAndYearlyValues(!displayQuarterlyAndYearlyValues);

        loggingService.trackEventWithAnalytics(
            EventActionsEnum.ToggleClicked,
            EventCategoriesEnum.HideOrDisplayData,
            '',
            EventDataTargetsEnum.SeasonalDataTables,
        );
    };

    const [displayDecimalPlacesMinimum, displayDecimalPlacesMaximum] = useMemo(() => {
        const minimumDecimalPlaces = tableDefinitionCommodity?.displayDecimalPlacesMinimum ?? 0;
        const maximumDecimalPlaces = tableDefinitionCommodity?.displayDecimalPlacesMaximum ?? 0;

        if (props.isNativeCurrency !== false && props.isNativeUnitOfMeasure !== false) {
            return [minimumDecimalPlaces, maximumDecimalPlaces];
        }

        const values = seasonalData?.rows?.flatMap((x) =>
            (x as SeasonalMonthlyRowModel).monthlyValues.filter((n) => n !== null && n !== undefined && n.value !== 0).map((m) => m.value as number),
        );
        if (values && values.length > 0) {
            const minimumValue = Math.min(...values);
            const decimalPlaces = formattingService.getDisplayDecimalPlacesMinimum(minimumValue);
            return [decimalPlaces, decimalPlaces];
        }

        return [0, 0];
    }, [seasonalData, props.isNativeCurrency, props.isNativeUnitOfMeasure]);

    // Resync the years state when we at the end of api data.
    useEffect(() => {
        const yearsFromData = seasonalData?.rows?.map((y) => y.year);

        if (years?.every((x) => yearsFromData?.includes(x))) {
            return;
        }

        setYears(yearsFromData!);
    }, [seasonalData]);

    const componentMenuValueOptions = useMemo(() => {
        const menuOptions: INestedOption<ComponentHeaderRightSideOptions>[] = [];
        const downloadOptions: INestedOption<ComponentHeaderRightSideOptions>[] = [];

        if (props.isXlsxDownloadAvailable) {
            downloadOptions.push({
                key: ComponentHeaderRightSideOptions.ValueXlsx,
                label: translations.dropdown.downloads.xlsx.toLocaleUpperCase(),
                value: ComponentHeaderRightSideOptions.ValueXlsx,
            });
        }

        downloadOptions.push({
            key: ComponentHeaderRightSideOptions.ValueCsv,
            label: translations.dropdown.downloads.csv.toLocaleUpperCase(),
            value: ComponentHeaderRightSideOptions.ValueCsv,
        });

        menuOptions.push({
            key: ComponentHeaderRightSideOptions.ChartDownload,
            label: (
                <div className={styles.component_more_options_label_container}>
                    <div>{translations.dropdown.downloadAs}</div>
                </div>
            ),
            value: ComponentHeaderRightSideOptions.ChartDownload,
            options: downloadOptions,
        });

        return menuOptions;
    }, [props.isXlsxDownloadAvailable]);

    const componentMenuPercentOptions = useMemo(() => {
        const menuOptions: INestedOption<ComponentHeaderRightSideOptions>[] = [];
        const downloadOptions: INestedOption<ComponentHeaderRightSideOptions>[] = [];

        if (props.isXlsxDownloadAvailable) {
            downloadOptions.push({
                key: ComponentHeaderRightSideOptions.PercentXlsx,
                label: translations.dropdown.downloads.xlsx.toLocaleUpperCase(),
                value: ComponentHeaderRightSideOptions.PercentXlsx,
            });
        }

        downloadOptions.push({
            key: ComponentHeaderRightSideOptions.PercentCsv,
            label: translations.dropdown.downloads.csv.toLocaleUpperCase(),
            value: ComponentHeaderRightSideOptions.PercentCsv,
        });

        menuOptions.push({
            key: ComponentHeaderRightSideOptions.ChartDownload,
            label: (
                <div className={styles.component_more_options_label_container}>
                    <div>{translations.dropdown.downloadAs}</div>
                </div>
            ),
            value: ComponentHeaderRightSideOptions.ChartDownload,
            options: downloadOptions,
        });

        return menuOptions;
    }, [props.isXlsxDownloadAvailable]);

    const handleMoreOptionsSelection = (value: ComponentHeaderRightSideOptions) => {
        const downloadData = buildSeasonalCsv(value as SeasonalTableReferenceType);

        switch (value) {
            case ComponentHeaderRightSideOptions.PercentCsv:
            case ComponentHeaderRightSideOptions.ValueCsv:
                tableCsvDownloadService.downloadCsv(downloadData!, props.title);
                break;
            case ComponentHeaderRightSideOptions.PercentXlsx:
            case ComponentHeaderRightSideOptions.ValueXlsx:
                tableCsvDownloadService.downloadXlsx(downloadData!, props.title);
                break;
            default:
                break;
        }
    };

    return (
        <div data-testid={props.testId} className={styles.seasonal_table_wrapper_container}>
            <div className={styles.seasonal_table_title_container}>
                <ComponentHeader title={props.title} subtitle={pageSubtitle} useContainerFontSize />
                <div className={styles.seasonal_table_button_container}>
                    {seasonalData?.averages?.find((x) => x.numberOfYears === 3) && (
                        <>
                            <Switch
                                handleChange={(value) => setDisplayThreeYearAverageValues(value)}
                                testId={`${props.testId}ThreeYearAverageSwitch`}
                                track={{
                                    eventCategory: props.track.eventCategory,
                                    eventAction: `${props.track.eventAction}ThreeYearAverageSwitchChanged`,
                                }}
                            />
                            <p className={styles.seasonal_table_quarterly_and_yearly_text}>{translations.text.threeYearAverage}</p>
                        </>
                    )}
                    {seasonalData?.averages?.find((x) => x.numberOfYears === 5) && (
                        <>
                            <Switch
                                handleChange={(value) => setDisplayFiveYearAverageValues(value)}
                                testId={`${props.testId}FiveYearAverageSwitch`}
                                track={{
                                    eventCategory: props.track.eventCategory,
                                    eventAction: `${props.track.eventAction}FiveYearAverageSwitchChanged`,
                                }}
                            />
                            <p className={styles.seasonal_table_quarterly_and_yearly_text}>{translations.text.fiveYearAverage}</p>
                        </>
                    )}
                    <Switch
                        handleChange={handleDisplayAveragesToggle}
                        testId={`${props.testId}QuarterlyAndYearlySwitch`}
                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}QuarterlyAndYearlyChanged` }}
                    />

                    <p className={styles.seasonal_table_quarterly_and_yearly_text}>{translations.text.quarterlyAndYearly}</p>
                    <div className={styles.seasonal_table_add_and_remove_years}>
                        <LinkButton
                            onClick={handleAddYears}
                            title={translations.actions.addFiveYears}
                            disabled={!seasonalData}
                            type={LinkButtonType.White}
                            testId={`${props.testId}AddFiveYearsButton`}
                            track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}AddFiveYearsClicked` }}
                        />
                    </div>
                    <div className={styles.seasonal_table_add_and_remove_years}>
                        <LinkButton
                            onClick={handleRemoveYears}
                            title={translations.actions.removeFiveYears}
                            type={LinkButtonType.White}
                            disabled={!seasonalData?.rows || seasonalData.rows.length <= 5}
                            testId={`${props.testId}RemoveFiveYearsButton`}
                            track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}RemoveFiveYearsClicked` }}
                        />
                    </div>
                    {!hasRestrictedDataSource && (
                        <div className={styles.seasonal_table_download_dropdown}>
                            <IconMenuMultiStageDropdown
                                name="SeasonalTablesMultiStageDropdown"
                                tooltip={translations.dropdown.moreOptions}
                                iconButton={<MoreHorizOutlinedIcon sx={{ color: scssVariables.mainTextColor }} />}
                                handleSelection={handleMoreOptionsSelection}
                                options={componentMenuValueOptions}
                                testId={props.testId}
                                track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}MoreOptions` }}
                            />
                        </div>
                    )}
                </div>
            </div>
            <SeasonalTableRaw
                seasonalData={seasonalData!}
                isPercent={false}
                displayThreeYearValues={displayThreeYearAverageValues}
                displayFiveYearValues={displayFiveYearAverageValues}
                displayQuarterlyAndYearlyValues={displayQuarterlyAndYearlyValues}
                displayDecimalPlacesMinimum={displayDecimalPlacesMinimum}
                displayDecimalPlacesMaximum={displayDecimalPlacesMaximum}
            />
            {props.showPercentTable && (
                <>
                    <div className={styles.seasonal_table_title_container}>
                        <div className={styles.seasonal_table_button_percent_container}>
                            {!hasRestrictedDataSource && (
                                <div className={styles.seasonal_table_download_dropdown}>
                                    <IconMenuMultiStageDropdown
                                        name="SeasonalTablesMultiStageDropdownPercent"
                                        tooltip={translations.dropdown.moreOptions}
                                        iconButton={<MoreHorizOutlinedIcon sx={{ color: scssVariables.mainTextColor }} />}
                                        handleSelection={handleMoreOptionsSelection}
                                        options={componentMenuPercentOptions}
                                        testId={props.testId}
                                        track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}MoreOptions` }}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <SeasonalTableRaw
                        seasonalData={seasonalData!}
                        isPercent
                        displayQuarterlyAndYearlyValues={displayQuarterlyAndYearlyValues}
                        displayDecimalPlacesMinimum={displayDecimalPlacesMinimum}
                        displayDecimalPlacesMaximum={displayDecimalPlacesMaximum}
                    />
                </>
            )}
            <DataSourceTag value={seasonalData?.dataSourceTag} />
            {showExportModal && (
                <AlertModal
                    setShowModal={setShowExportModal}
                    message={
                        <h3>
                            {translations.errors.downloadSubscriptionError}{' '}
                            <EmailLink
                                linkText={translations.text.brokerSupport}
                                email={applicationSettings.emails.supportEmail}
                                track={{ eventCategory: props.track.eventCategory, eventAction: `${props.track.eventAction}SupportEmail` }}
                            />
                        </h3>
                    }
                />
            )}
        </div>
    );
};

export default SeasonalTables;
